import { RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, QueryKey, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmptyDocumentContent } from '@confinion/confideck-shared';

import { ApiContext } from '@shared/contexts';
import {
  THeading,
  TDocument,
  TDraft,
  TInRef,
  TRefData,
  TReference,
  TVersion,
  TPlacedBlock,
  TInitContentDto,
  TVariant,
  TUseDocumentResult,
  TCreateVariantReturn,
  TPlaceBlockReturn,
  TDocumentConfigDto,
  TAttachmentUpdateReturn,
  TBlockData,
  TDocumentAttachment,
  TEditDraftReturn,
  TBlockSaveInfo,
  TSaveReturn,
  TSelectedTaggedBlock,
  TTemplateField,
} from './types';
import { TFieldValuesMap } from './helpers/extractFieldValues';
import { composeTemplateDocument } from '@shared/backendFrontend/composeTemplateDocumentFe';
import { TTemplateBlobInfo } from './types/documents.types';
import { TResolvedLiteratureCitation } from './types/reference.types';
import { TItem, TStats, TVersionInfo, useRefetchItemStore, EBindingMode } from '@modules/items';
import {
  addItemsToFolderQueryData,
  updateCustomFieldDefaultQueryData,
  updateCustomFieldDefaultsEnabledQueryData,
} from '../items/api/itemQueryData';
import { TLiterature, EAssessmentDecision } from '@modules/literatures';
import { SignInContext } from '@modules/users';
import { TCustomFieldRaw } from '@modules/users/types/organizations.types';
import getDocumentProductId from './helpers/getDocumentProductId';
import resolveLiteratureDecision from './helpers/resolveLiteratureDecision.helper';
import { TTreeHeading } from './tiptap/nodes/heading/HeadingNode';
import DocumentContext from './contexts/DocumentContext.context';
import { TMap } from '@shared/types';
import { areArraysEqual } from '@shared/helpers';
import { Editor } from '@tiptap/core';
import { isLargeDocument } from './largeDocument';
import insertSpaceIntoEmptyTags from './helpers/insertSpaceIntoEmptyTags.helper';
import { withRefsData } from './helpers/withRefsData.helper';
import type { TScrollbarElem } from '@shared/components/CustomScrollbar';
import { TERMINOLOGY } from '@shared/i18n';
import { useNavigate } from 'react-router-dom';
import useLoadDocumentContent from './hooks/useLoadDocumentContent.hook';

// --- GET:

export const useDocument = (id: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const refetchEnabled = useRefetchItemStore((state) => state.refetchEnabled);

  const getDocument = async (signal?: AbortSignal): Promise<TUseDocumentResult> => {
    const res = await apiClient.get(`/documents/${id}`, { signal });
    res.data.doc.versions.reverse();
    return res.data;
  };

  const { data, isError, error } = useQuery({
    retry,
    queryKey: [`document-${id}`],
    queryFn: ({ signal }) => getDocument(signal),
    refetchOnWindowFocus: refetchEnabled,
    refetchOnMount: refetchEnabled,
    refetchOnReconnect: refetchEnabled,
  });
  useHandleQueryError(isError, error, `Failed to load document ${id}!`, 'Loading document failed!');

  return { error, data };
};

type TOwnedDocumentQuery = {
  // The owning feature's key: it holds every input of `url` and `params`, and the feature's mutations invalidate it.
  queryKey: QueryKey;
  documentId: string | undefined;
  url: string;
  errorMessage: string;
  params?: Record<string, string | undefined>;
};

// A document that another feature owns, read through that feature's route.
export const useOwnedDocument = ({ queryKey, documentId, url, errorMessage, params }: TOwnedDocumentQuery) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const refetchEnabled = useRefetchItemStore((state) => state.refetchEnabled);
  const documentKey = [`document-${documentId}`];
  const scoped = useQuery({
    queryKey,
    enabled: !!documentId,
    retry,
    // Back on an earlier version, a kept entry would show the shared `document-<id>` payload of the version just left.
    gcTime: 0,
    refetchOnWindowFocus: refetchEnabled,
    refetchOnMount: refetchEnabled,
    refetchOnReconnect: refetchEnabled,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<TUseDocumentResult>(url, { signal, params });
      data.doc.versions.reverse();
      // The editor reads and updates the `document-<id>` entry, as `useDocument` fills it.
      queryClient.setQueryData(documentKey, data);
      return data;
    },
  });
  const { data } = useQuery<TUseDocumentResult>({ queryKey: documentKey, queryFn: skipToken });
  useHandleQueryError(scoped.isError, scoped.error, errorMessage, true);
  // A failed refetch keeps the open editor; another document or version shows nothing until it has loaded.
  return { data: scoped.data ? data : undefined, error: scoped.error };
};

// Keep blob GETs as simple CORS requests; custom no-cache headers trigger preflight against Azurite.
const getBlobRequestUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}_cb=${Date.now()}`;

export type TContentStatus = 'LOADING' | 'LOADED' | 'BLOCKED' | 'ERROR';
type TUseBlobReturn = {
  contentStatus: TContentStatus;
  content: string | undefined;
};
type TUseContentOptions = {
  editor: Editor | null;
  refAutoFocusCancelled: RefObject<boolean>;
  /** Decides whether this document loads in parts - see `isPartLoadable`. */
  doc: TDocument;
  /** Same decision: a bound version keeps the old path. */
  version?: TDraft | TVersion;
  isEdit: boolean;
  retried: boolean;
  refContentNode: RefObject<TScrollbarElem>;
  token: string;
  containerName: string;
  blobName?: string;
  blocked?: boolean;
  templateBlobInfo?: TTemplateBlobInfo;
  templateBlobName?: string; // resolved blob name for the viewed template version
};

export const useContent = ({
  editor,
  refAutoFocusCancelled,
  doc,
  version,
  isEdit,
  retried,
  refContentNode,
  token,
  containerName,
  blobName,
  blocked = false,
  templateBlobInfo,
  templateBlobName,
}: TUseContentOptions): TUseBlobReturn => {
  const { azureClient, useHandleQueryError, getAzureBlobUrl } = useContext(ApiContext);
  const refBlocked = useRef(blocked);
  refBlocked.current = blocked;

  const refCache = useRef('');

  const getBlob = async (): Promise<string> => {
    if (!blobName) return ''; // initial
    if (refBlocked.current) return refCache.current;

    if (templateBlobInfo && templateBlobName) {
      // PERF: template HTML re-fetched on every field values change (shared queryKey). Split into two queries when needed.
      // Bound: parallel fetch of template HTML + field values JSON, then compose
      const [tplRes, fvRes] = await Promise.all([
        azureClient.get(
          getBlobRequestUrl(
            getAzureBlobUrl(templateBlobInfo.containerId, templateBlobName, templateBlobInfo.versionBlobToken)
          )
        ),
        azureClient.get(getBlobRequestUrl(getAzureBlobUrl(containerName, blobName, token))),
      ]);
      // Defensive: axios auto-parses application/json, but Azure may serve with text/plain
      const fv = typeof fvRes.data === 'string' ? JSON.parse(fvRes.data) : fvRes.data;
      return composeTemplateDocument(tplRes.data, fv);
    }

    const res = await azureClient.get(getBlobRequestUrl(getAzureBlobUrl(containerName, blobName, token)));
    return res.data;
  };

  // Block refetch for 10s after returning from edit mode to compensate for Azure blob propagation delay.
  const [blockRefetch, setBlockRefetch] = useState<boolean | undefined>(undefined);
  const refTimeout = useRef<number | null>(null);
  useEffect(() => {
    if (blocked) {
      setBlockRefetch(true);
      if (refTimeout.current !== null) clearTimeout(refTimeout.current);
    } else {
      if (blockRefetch === undefined)
        setBlockRefetch(false); // initial
      else if (blockRefetch) {
        const timeoutId = setTimeout(() => {
          setBlockRefetch(false);
        }, 10000);
        refTimeout.current = timeoutId as any as number;
      }
    }
  }, [blocked]);

  const isBlockRefetch = !blocked && blockRefetch;

  const { data, isError, error, isFetching } = useQuery({
    queryKey: [`blob-${blobName}`],
    queryFn: getBlob,

    refetchOnWindowFocus: isBlockRefetch ? false : undefined,
    refetchOnMount: isBlockRefetch ? false : undefined,
    refetchInterval: isBlockRefetch ? false : undefined,
    refetchOnReconnect: isBlockRefetch ? false : undefined,
  });
  useHandleQueryError(
    isError,
    error,
    `Failed to load blob ${containerName}/${blobName}!`,
    'Loading document content failed!'
  );

  if (data) refCache.current = data;

  const contentStatus = isError ? 'ERROR' : blocked ? 'BLOCKED' : typeof data === 'string' ? 'LOADED' : 'LOADING';

  useLoadDocumentContent({
    editor,
    doc,
    version,
    data: contentStatus === 'LOADED' ? data : undefined,
    isFetching,
    blobName,
    isEdit,
  });

  useEffect(() => {
    // Every part swap sets content, and focusing the start would drag the caret to the part's top.
    if (isLargeDocument()) return;
    if ((isEdit && !retried && refContentNode.current?.view.scrollTop === 0) || isEmptyDocumentContent(data || '')) {
      // no scroll or new document
      const timeoutId = window.setTimeout(() => {
        if (
          !editor ||
          editor.isDestroyed ||
          !editor.isEditable ||
          refAutoFocusCancelled.current ||
          editor.view.hasFocus()
        )
          return;
        editor.commands.focus('start');
      }, 100);

      return () => window.clearTimeout(timeoutId);
    }
    // editor.isEditable is mutable and does not trigger a render; isEdit is the React transition.
  }, [isEdit]);

  return useMemo(() => ({ contentStatus, content: data }), [contentStatus, data]);
};

export const useGetContent = () => {
  const queryClient = useQueryClient();
  const { azureClient, handleApiError, getAzureBlobUrl } = useContext(ApiContext);

  const mutationFn = async (variables: { token: string; containerName: string; blobName: string }): Promise<string> => {
    const res = await azureClient.get(
      getBlobRequestUrl(getAzureBlobUrl(variables.containerName, variables.blobName, variables.token))
    );
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to load content for editing draft!', true),
    onSuccess(data, variables) {
      // if initial empty document with no content is completed (no save is executed)
      queryClient.setQueryData([`blob-${variables.blobName}`], data);
    },
  });
};

/**
 * Bound-doc equivalent of useGetContent.
 * Fetches template HTML + field values JSON in parallel, composes them, and seeds the blob cache.
 * Used when entering edit mode for a bound document to ensure the editor always starts from
 * the DRAFT's current field values rather than a previously viewed version's cached content.
 */
export const useGetBoundContent = () => {
  const queryClient = useQueryClient();
  const { azureClient, handleApiError, getAzureBlobUrl } = useContext(ApiContext);

  const mutationFn = async (variables: {
    templateBlobInfo: TTemplateBlobInfo;
    templateBlobName: string;
    token: string;
    containerName: string;
    fieldValuesBlobName: string;
  }): Promise<string> => {
    const { templateBlobInfo, templateBlobName, token, containerName, fieldValuesBlobName } = variables;
    const [tplRes, fvRes] = await Promise.all([
      azureClient.get(
        getBlobRequestUrl(
          getAzureBlobUrl(templateBlobInfo.containerId, templateBlobName, templateBlobInfo.versionBlobToken)
        )
      ),
      azureClient.get(getBlobRequestUrl(getAzureBlobUrl(containerName, fieldValuesBlobName, token))),
    ]);
    // Defensive: axios auto-parses application/json, but Azure may serve with text/plain
    const fv = typeof fvRes.data === 'string' ? JSON.parse(fvRes.data) : fvRes.data;
    return composeTemplateDocument(tplRes.data, fv);
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to load content for editing draft!', true),
    onSuccess(data, variables) {
      queryClient.setQueryData([`blob-${variables.fieldValuesBlobName}`], data);
    },
  });
};

export const useBlob = (token: string, containerName: string, blobName?: string) => {
  const { azureClient, useHandleQueryError, getAzureBlobUrl, retry } = useContext(ApiContext);

  const getBlob = async (): Promise<string> => {
    if (!blobName) return '<PLACEHOLDER>';
    const res = await azureClient.get(getBlobRequestUrl(getAzureBlobUrl(containerName, blobName, token)));
    return res.data;
  };

  const { data, isError, error } = useQuery({
    retry,
    queryKey: [blobName ? `blob-${blobName}` : 'none'],
    queryFn: getBlob,
  });
  useHandleQueryError(isError, error, `Failed to load blob ${containerName}/${blobName}!`);

  return { data, error };
};

export const useGetBlob = () => {
  const queryClient = useQueryClient();
  const { azureClient, handleApiError, getAzureBlobUrl } = useContext(ApiContext);

  const mutationFn = async (variables: { token: string; containerName: string; blobName: string }): Promise<string> => {
    const res = await azureClient.get(
      getBlobRequestUrl(getAzureBlobUrl(variables.containerName, variables.blobName, variables.token))
    );
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to load item!', true),
    onSuccess(data, variables) {
      // if initial empty document with no content is completed (no save is executed)
      queryClient.setQueryData([`blob-${variables.blobName}`], data);
    },
  });
};

// --- POST:

export const useCreate = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: {
    parent: string;
    name: string;
    initContent?: TInitContentDto;
  }): Promise<TItem> => {
    const res = await apiClient.post(`/documents`, variables);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (item: TItem, variables) => {
      show('success', `${item.isBlock ? TERMINOLOGY.block.upper : 'Document'} created.`);
      addItemsToFolderQueryData(queryClient, variables.parent, [item]);
    },
    onError: handleApiError('Creating document failed!'),
  });
};

export const useUploadImage = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: { documentId: string; file: File }): Promise<string> => {
    const formData = new FormData();
    formData.append('file', variables.file);
    const res = await apiClient.post(`/documents/${variables.documentId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  return useMutation({ mutationFn, onError: handleApiError('Uploading image failed!', true) });
};

export const useCopyBetweenDocs = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (vars: { fromDocId: string; toDocId: string; blobName: string }): Promise<string> => {
    const { data } = await apiClient.post('/documents/images/copy-between-docs', vars);
    return data; // new blobName
  };

  return useMutation({ mutationFn, onError: handleApiError('Copying image failed!', true) });
};

export const useExport = () => {
  const { showProgress, apiClient, handleApiError, queryClient } = useContext(ApiContext);

  const mutationFn = async (variables: {
    documentId: string;
    versionId: string | 'draft';
  }): Promise<TDraft | TVersion> => {
    const hideProgress = showProgress('Exporting...');
    try {
      const res = await apiClient.post(`/documents/${variables.documentId}/export`, {
        version: variables.versionId,
      });
      return res.data;
    } finally {
      hideProgress();
    }
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Export failed!', true),
    onSuccess(data, variables) {
      queryClient.setQueryData(
        [`document-${variables.documentId}`],
        (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined =>
          oldData && {
            ...oldData,
            doc: {
              ...oldData.doc,
              draft:
                variables.versionId === 'draft'
                  ? { ...(data as TDraft), selectedTaggedBlocks: oldData.doc.draft?.selectedTaggedBlocks ?? [] }
                  : oldData.doc.draft,
              versions: oldData.doc.versions.map((v) =>
                v.id === variables.versionId
                  ? { ...(data as TVersion), selectedTaggedBlocks: v.selectedTaggedBlocks }
                  : v
              ),
            },
          }
      );
    },
  });
};

export const useAddRef = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: { docId: string; ref: TReference }): Promise<TRefData> => {
    return apiClient.post(`/documents/${variables.docId}/reference`, variables.ref).then((res) => res.data);
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Failed to create ${TERMINOLOGY.reference.lower}!`, true),
    onSuccess(data, { docId, ref }) {
      const newInRef: TInRef | null =
        ref.itemId === docId ? { id: ref.id, sourceDocId: docId, sourceDocName: '', headingId: ref.headingId } : null;
      queryClient.setQueryData([`document-${docId}`], (oldData: TUseDocumentResult): TUseDocumentResult => {
        // A SELF reference has no entry in refsData; only its id goes to outRefs.
        const withRef =
          ref.versionMode === 'SELF'
            ? {
                ...oldData.doc,
                draft: {
                  ...(oldData.doc.draft as TDraft),
                  outRefs: [...(oldData.doc.draft as TDraft).outRefs, ref.id],
                },
              }
            : withRefsData(oldData.doc, { [ref.id]: data });
        return { ...oldData, doc: { ...withRef, inRefs: newInRef ? [...withRef.inRefs, newInRef] : withRef.inRefs } };
      });
    },
  });
};

export const usePlaceBlock = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: { docId: string; block: TPlacedBlock }): Promise<TPlaceBlockReturn> => {
    return apiClient.post(`/documents/${variables.docId}/block`, variables.block).then((res) => res.data);
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Failed to add ${TERMINOLOGY.block.lower}!`, true),
    onSuccess(data, { docId, block }) {
      queryClient.setQueryData(
        [`document-${docId}`],
        (oldData: TUseDocumentResult): TUseDocumentResult => ({
          ...oldData,
          doc: {
            ...oldData.doc,
            draft: {
              ...(oldData.doc.draft as TDraft),
              blocks: [...(oldData.doc.draft as TDraft).blocks, block],
            },
            blocksData: { ...oldData.doc.blocksData, [block.id]: data.blockData },
          },
          resolvedItemNames: { ...oldData.resolvedItemNames, ...data.resolvedItemNames },
        })
      );
    },
  });
};

// Pre-flight only (citations are flat IDs, so save() re-derives draft.literatureCitations from body) - cache patch is optimistic ~1 autosave ahead of persistence.
export const useAddLiteratureCitation = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const { organization } = useContext(SignInContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: {
    doc: TDocument;
    literature: TLiterature;
  }): Promise<TResolvedLiteratureCitation> => {
    await apiClient.post(`/documents/${variables.doc._id}/literature`, { literatureId: variables.literature._id });
    const latestVersion = variables.literature.versions[0];
    // A product doc is INCLUDED because the validator just required it; a QM/country-reg doc has no product to assess against.
    const decision = resolveLiteratureDecision(
      !!getDocumentProductId(variables.doc, organization),
      EAssessmentDecision.INCLUDED
    );
    return {
      _id: variables.literature._id,
      name: latestVersion?.content.title ?? '',
      archived: variables.literature.archived,
      decision,
      latestVersionId: latestVersion?.id,
      versions: latestVersion ? { [latestVersion.id]: latestVersion.content } : {},
    };
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Adding ${TERMINOLOGY.literature.lower} citation failed!`, true),
    onSuccess: (statusEntry, variables) => {
      queryClient.setQueryData(
        [`document-${variables.doc._id}`],
        (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined =>
          oldData
            ? {
                ...oldData,
                doc: {
                  ...oldData.doc,
                  resolvedLiteratureCitations: {
                    ...(oldData.doc.resolvedLiteratureCitations ?? {}),
                    [statusEntry._id]: statusEntry,
                  },
                },
              }
            : oldData
      );
    },
  });
};

export const useCreateVariant = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: {
    docId: string;
    folder?: string;
    srcVariantFolder?: string;
  }): Promise<TCreateVariantReturn | void> => {
    const res = await apiClient.post(`/documents/${variables.docId}/variant`, {
      folder: variables.folder,
      srcVariantFolder: variables.srcVariantFolder,
    });
    const data: TCreateVariantReturn | undefined = res.data;
    queryClient.setQueryData([`document-${variables.docId}`], (oldData: TUseDocumentResult): TUseDocumentResult => {
      if (data)
        return {
          ...oldData,
          doc: {
            ...oldData.doc,
            draft: {
              ...(oldData.doc.draft as TDraft),
              variants: [...(oldData.doc.draft as TDraft).variants, data.variant],
            },
          },
          resolvedItemNames: { ...oldData.resolvedItemNames, [data.variant.folder]: data.folderName },
        };
      return {
        ...oldData,
        doc: { ...oldData.doc, draft: { ...(oldData.doc.draft as TDraft), hasDefaultVariant: true } },
      };
    });
    return data;
  };

  return useMutation({ mutationFn, onError: handleApiError('Failed to add variant!', true) });
};

// --- PUT:

export const useEditDraft = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string }): Promise<TEditDraftReturn> => {
    const { data }: { data: TEditDraftReturn } = await apiClient.put(`/documents/${variables.id}/draft`);

    // Must be synchronous: cache update must complete before the query refetch triggered by invalidation.
    if (data.draft && data.stats) {
      queryClient.setQueryData([`document-${variables.id}`], (oldData: TUseDocumentResult): TUseDocumentResult => {
        let newBlocksData = oldData.doc.blocksData;
        if (!oldData.doc.draft) {
          newBlocksData = {};
          const latestId = oldData.doc.versions[0].id;
          for (const blockId in oldData.doc.blocksData) {
            const blockData = oldData.doc.blocksData[blockId];
            const blockVersions = { ...blockData.versions };
            if (blockData.versions[latestId]) blockVersions.draft = { ...blockData.versions[latestId] };
            newBlocksData[blockId] = { ...blockData, versions: blockVersions };
          }
        }

        return {
          // update doc:
          ...oldData,
          doc: {
            ...oldData.doc,
            draft: data.draft as TDraft,
            stats: data.stats as TStats,
            blocksData: newBlocksData,
          },
        };
      });
      // update version-info query cache
      queryClient.setQueryData(
        [`version-info-${variables.id}`],
        (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
          oldData ? { ...oldData, draft: data.draft } : oldData
      );
    }

    return data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to edit draft!', true),
  });
};

export const useUpdateBlockContent = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async ({
    id,
    ...initContent
  }: TInitContentDto & { id: string }): Promise<{ draft: TDraft; stats: TStats }> => {
    const res = await apiClient.put(`/documents/${id}/block-content`, initContent);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Updating ${TERMINOLOGY.block.lower} failed!`, true),
    onSuccess: ({ draft, stats }, { id }) => {
      show('success', `${TERMINOLOGY.block.upper} updated.`);
      queryClient.setQueryData([`document-${id}`], (oldData: TUseDocumentResult | undefined) =>
        oldData ? { ...oldData, doc: { ...oldData.doc, draft, stats } } : oldData
      );
      queryClient.setQueryData([`version-info-${id}`], (oldData: TVersionInfo | undefined) =>
        oldData ? { ...oldData, draft } : oldData
      );
    },
  });
};

type TBaseSaveVars = {
  doc: TDocument;
  title: string;
  headings: THeading[];
  outRefIds: string[];
  literatureIds: string[];
  blocksSaveInfo: TBlockSaveInfo[];
};

type TBoundSaveVars = TBaseSaveVars & {
  fieldValues: TFieldValuesMap;
  cacheHtml: string;
  html?: never;
  variant?: never;
  templateFields?: never;
};

type TRegularSaveVars = TBaseSaveVars & {
  html: string;
  variant?: TVariant;
  templateFields?: TTemplateField[];
  fieldValues?: never;
  cacheHtml?: never;
};

type TSaveVars = TBoundSaveVars | TRegularSaveVars;

export const useSave = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: TSaveVars): Promise<TSaveReturn> => {
    const formData = new FormData();

    if (variables.fieldValues) {
      // BOUND SAVE: upload field values JSON as the file
      const jsonStr = JSON.stringify(variables.fieldValues);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      formData.append('file', blob, 'field-values.json');
      formData.append(
        'json',
        JSON.stringify({
          title: variables.title,
          headings: variables.headings,
          outRefIds: variables.outRefIds,
          literatureIds: variables.literatureIds,
          blocksSaveInfo: variables.blocksSaveInfo,
        })
      );
    } else {
      // REGULAR SAVE: upload HTML blob
      const blob = new Blob([variables.html], { type: 'text/html' });
      formData.append('file', blob, 'content.html');
      formData.append(
        'json',
        JSON.stringify({
          title: variables.title,
          headings: variables.headings,
          outRefIds: variables.outRefIds,
          literatureIds: variables.literatureIds,
          blocksSaveInfo: variables.blocksSaveInfo,
          variantFolder: variables.variant?.folder,
          ...(variables.templateFields ? { templateFields: variables.templateFields } : {}),
        })
      );
    }

    const { data } = await apiClient.put(`/documents/${variables.doc._id}/save`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // Update blob cache (regular saves only - bound saves have no HTML blob)
    if (variables.html) {
      // Must be synchronous: cache update must complete before the query refetch triggered by invalidation.
      queryClient.setQueryData(
        [`blob-${variables.variant?.blobName || variables.doc.draft?.blobName}`],
        variables.html
      );
    }

    // Bound saves: cache the composed HTML so view mode sees fresh content without a stale flash.
    // Mirrors the regular-doc pattern - setQueryData here is synchronous and runs before the
    // blocked→false transition, so the content load fires exactly once with fresh data.
    if (variables.cacheHtml) {
      const fieldValuesBlobName = variables.doc.draft?.templateSource?.fieldValuesBlobName;
      if (fieldValuesBlobName) {
        queryClient.setQueryData([`blob-${fieldValuesBlobName}`], variables.cacheHtml);
      }
    }

    return data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to save changes!', true),
    onSuccess(data, { doc, title, outRefIds, literatureIds, headings, variant }) {
      if (!doc.draft) return;
      const hasUpdatedTitle = !variant && (doc.draft.title ?? '') !== title;
      const hasUpdatedHeadings = JSON.stringify(doc.draft.headings) !== JSON.stringify(headings);
      const hasUpdatedOutRefIds = !areArraysEqual(doc.draft.outRefs, outRefIds);
      const hasUpdatedLiteratureIds = !areArraysEqual(doc.draft.literatureCitations ?? [], literatureIds);
      if (
        !data.newInvalidRefIds &&
        !data.newDraftBlocks &&
        !data.resolvedLiteratureCitations &&
        !hasUpdatedTitle &&
        !hasUpdatedOutRefIds &&
        !hasUpdatedLiteratureIds &&
        !hasUpdatedHeadings
      )
        return; // no changes

      queryClient.setQueryData(
        [`document-${doc._id}`],
        (oldData: TUseDocumentResult): TUseDocumentResult | undefined => {
          if (!oldData) return oldData;

          const newDoc = { ...oldData.doc };
          if (data.newInvalidRefIds) newDoc.invalidRefIds = data.newInvalidRefIds;
          if (data.resolvedLiteratureCitations)
            newDoc.resolvedLiteratureCitations = {
              ...oldData.doc.resolvedLiteratureCitations,
              ...data.resolvedLiteratureCitations,
            };

          if (!newDoc.draft) return oldData;

          if (data.newDraftBlocks) newDoc.draft = { ...newDoc.draft, blocks: data.newDraftBlocks };

          if (hasUpdatedTitle) newDoc.draft = { ...newDoc.draft, title };

          if (hasUpdatedHeadings) newDoc.draft = { ...newDoc.draft, headings };

          if (hasUpdatedOutRefIds) newDoc.draft = { ...newDoc.draft, outRefs: outRefIds };

          if (hasUpdatedLiteratureIds) newDoc.draft = { ...newDoc.draft, literatureCitations: literatureIds };

          return { ...oldData, doc: newDoc };
        }
      );
    },
  });
};

export const useComplete = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    versionId: string;
    note: string;
    isEditing: boolean;
  }): Promise<TVersion> => {
    const res = await apiClient.put(`/documents/${variables.id}/complete`, {
      version: variables.versionId,
      note: variables.note,
      isEditing: variables.isEditing,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to complete version!', true),
    onSuccess: (data, variables, context) => {
      // update query-data must be done in order, and is therefore done by the caller
      show('success', `Completed draft and created version ${variables.versionId}.`);
    },
  });
};

export const getDocumentQueryData = (queryClient: QueryClient, docId: string) =>
  queryClient.getQueryData<TUseDocumentResult>([`document-${docId}`]);

export const updateCompleteQueryData = (queryClient: QueryClient, docId: string, newVersion: TVersion) => {
  // document query cache
  queryClient.setQueryData([`document-${docId}`], (oldData: TUseDocumentResult): TUseDocumentResult => {
    const newBlocksData: TMap<string, TBlockData> = {};
    for (const blockId in oldData.doc.blocksData) {
      const blockData = oldData.doc.blocksData[blockId];
      if (blockData.versions.draft)
        newBlocksData[blockId] = {
          ...blockData,
          versions: {
            ...blockData.versions,
            [newVersion.id]: { ...blockData.versions.draft },
          },
        };
    }

    return {
      ...oldData,
      doc: {
        ...oldData.doc,
        versions: [newVersion, ...oldData.doc.versions],
        comments: oldData.doc.comments.map((comment) => {
          if (comment.versionCreated === 'draft') comment.versionCreated = newVersion.id;
          if (comment.versionResolved === 'draft') comment.versionResolved = newVersion.id;
          return comment;
        }),
        draft: undefined,
        stats: { ...oldData.doc.stats, latestVersionId: newVersion.id },
        blocksData: newBlocksData,
      },
    };
  });
  // version-info query cache
  queryClient.setQueryData([`version-info-${docId}`], (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
    oldData
      ? {
          ...oldData,
          draft: undefined,
          // no need to update `approvals` here, as they are not updated in the backend
          versions: [newVersion, ...(oldData.versions as TVersion[])],
        }
      : oldData
  );
};

const updateAttachmentQueryData = (queryClient: QueryClient, docId: string, data: TAttachmentUpdateReturn) => {
  queryClient.setQueryData(
    [`document-${docId}`],
    (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined =>
      oldData
        ? {
            ...oldData,
            doc: {
              ...oldData.doc,
              attachments: data.attachments,
              draft: data.draft,
              stats: data.stats,
            },
          }
        : oldData
  );

  queryClient.setQueryData([`version-info-${docId}`], (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
    oldData
      ? {
          ...oldData,
          draft: data.draft,
        }
      : oldData
  );
};

export const useEnableAttachments = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string }): Promise<TAttachmentUpdateReturn> =>
    apiClient.put(`/documents/${variables.id}/attachments/enable`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to enable attachments!', true),
    onSuccess: (data, variables) => {
      show('success', 'Attachments enabled.');
      updateAttachmentQueryData(queryClient, variables.id, data);
    },
  });
};

export const useAddAttachments = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string; files: File[] }): Promise<TAttachmentUpdateReturn> => {
    const formData = new FormData();
    variables.files.forEach((file) => formData.append('files', file));

    const { data } = await apiClient.post(`/documents/${variables.id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to upload attachments!', true),
    onSuccess: (data, variables) => {
      show('success', `Added ${variables.files.length} attachment${variables.files.length === 1 ? '' : 's'}.`);
      updateAttachmentQueryData(queryClient, variables.id, data);
    },
  });
};

export const useRemoveAttachmentFromDraft = (opts?: { showSuccessToast?: boolean }) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string; attachmentId: string }): Promise<TAttachmentUpdateReturn> =>
    apiClient.delete(`/documents/${variables.id}/attachments/${variables.attachmentId}`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to remove attachment!', true),
    onSuccess: (data, variables) => {
      const removedAttachment = data.attachments?.find(
        (attachment: TDocumentAttachment) => attachment._id === variables.attachmentId
      );
      if (opts?.showSuccessToast !== false) {
        show(
          'success',
          removedAttachment
            ? `Removed "${removedAttachment.fileName}" from the draft.`
            : 'Removed attachment from the draft.'
        );
      }
      updateAttachmentQueryData(queryClient, variables.id, data);
    },
  });
};

export const useRemoveAttachmentsFromDraft = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string; attachmentIds: string[] }): Promise<TAttachmentUpdateReturn> =>
    apiClient
      .delete(`/documents/${variables.id}/attachments`, { data: { attachmentIds: variables.attachmentIds } })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to remove attachments!', true),
    onSuccess: (data, variables) => {
      show(
        'success',
        `Removed ${variables.attachmentIds.length} attachment${variables.attachmentIds.length === 1 ? '' : 's'} from the draft.`
      );
      updateAttachmentQueryData(queryClient, variables.id, data);
    },
  });
};

export const useUpdateConfig = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const documentContext = useContext(DocumentContext); // doc context is undefined for files, so this hook is only used in documents

  const mutationFn = async (variables: { id: string; config: TDocumentConfigDto }): Promise<void> => {
    const res = await apiClient.put(`/documents/${variables.id}/config`, variables.config);
    return res.data;
  };

  return useMutation({
    mutationFn,
    // Optimistic update: immediately apply config to prevent layout flash when switching orientation.
    // This is the only mutation in the codebase using this pattern - justified because orientation
    // changes trigger visible container resizing, and waiting for the API would cause a jarring flash.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [`document-${variables.id}`] });
      const previous = queryClient.getQueryData<TUseDocumentResult>([`document-${variables.id}`]);
      queryClient.setQueryData(
        [`document-${variables.id}`],
        (oldData: TUseDocumentResult): TUseDocumentResult | undefined =>
          oldData
            ? {
                ...oldData,
                doc: { ...oldData.doc, draft: { ...(oldData.doc.draft as TDraft), config: variables.config } },
              }
            : undefined
      );
      return { previous };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData([`document-${variables.id}`], context.previous);
      }
      handleApiError('Failed to update document settings!', true)(err, variables, context);
    },
    onSuccess: (data, variables) => {
      show('success', `Updated document settings.`);
      // Force heading re-numbering by triggering the gate
      const editor = documentContext?.editor;
      if (!editor) return;
      const { tr } = editor.state;
      tr.setMeta('forceHeadingUpdate', true);
      editor.view.dispatch(tr);
    },
  });
};

const updateOriginalVersionQueryData = (queryClient: QueryClient, id: string, versionId: string, changed: boolean) => {
  const cachedDocument = queryClient.getQueryData<TUseDocumentResult>([`document-${id}`]);
  const resetPdf = changed || Boolean(cachedDocument && cachedDocument.doc.originalVersionId !== versionId);
  const updateDraft = (draft: TDraft | undefined) => (resetPdf && draft ? { ...draft, isPdfUpToDate: false } : draft);

  queryClient.setQueryData(
    [`document-${id}`],
    (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined =>
      oldData
        ? {
            ...oldData,
            doc: { ...oldData.doc, originalVersionId: versionId, draft: updateDraft(oldData.doc.draft) },
          }
        : oldData
  );
  queryClient.setQueryData([`version-info-${id}`], (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
    oldData ? { ...oldData, draft: updateDraft(oldData.draft) } : oldData
  );
};

export const useUpdateOriginalVersion = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  return useMutation({
    mutationFn: async ({ id, versionId }: { id: string; versionId: string }): Promise<boolean> => {
      const { data } = await apiClient.put(`/documents/${id}/original-version`, { version: versionId });
      return data;
    },
    onSuccess: (changed, { id, versionId }) => {
      updateOriginalVersionQueryData(queryClient, id, versionId, changed);
      show('success', `Original version was updated to v${versionId}.`);
    },
    onError: handleApiError(),
  });
};

export const useUpdateLastExternallyApproved = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string; file?: File | null }): Promise<TDocument> => {
    const formData = new FormData();
    if (variables.file) formData.append('file', variables.file);
    const { data } = await apiClient.put(`/documents/${variables.id}/last-externally-approved`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to update externally approved document!', true),
    onSuccess: (doc, variables) => {
      show('success', variables.file ? 'Uploaded approved PDF.' : 'Removed approved PDF.');
      queryClient.setQueryData(
        [`document-${variables.id}`],
        (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined =>
          oldData
            ? {
                ...oldData,
                doc: {
                  ...oldData.doc,
                  lastExternallyApproved: doc.lastExternallyApproved,
                  stats: { ...oldData.doc.stats, modifiedDate: doc.stats.modifiedDate },
                },
              }
            : oldData
      );
    },
  });
};

export const useConvertToFile = (redirectOnSuccess = true) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const navigate = useNavigate();

  const mutationFn = async (variables: { id: string }): Promise<string> =>
    apiClient.put(`/documents/${variables.id}/file`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to convert document to file!', true),
    onSuccess: (data, variables, context) => {
      show('success', `Updated document settings.`);
      if (redirectOnSuccess) navigate(`/file/${data}`, { replace: true }); // redirect to the new file
    },
  });
};

export const useConvertToTemplate = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: { id: string }): Promise<void> =>
    apiClient.put(`/documents/${variables.id}/template`).then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError(`Failed to convert document to ${TERMINOLOGY.template.lower}!`, true),
    onSuccess: (_data, variables) => {
      show('success', `Converted document to ${TERMINOLOGY.template.lower}.`);
      queryClient.setQueryData(
        [`document-${variables.id}`],
        (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined =>
          oldData ? { ...oldData, doc: { ...oldData.doc, isTemplate: true } } : oldData
      );
    },
  });
};

export const useSetCustomFieldDefaultsEnabled = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: { id: string; enabled: boolean }): Promise<void> =>
    apiClient
      .put(`/documents/${variables.id}/custom-field-defaults`, { enabled: variables.enabled })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError(`Saving ${TERMINOLOGY.template.lower} defaults failed!`, true),
    onSuccess: (_data, variables) => {
      show('success', `${TERMINOLOGY.template.upper} defaults ${variables.enabled ? 'enabled' : 'disabled'}.`);
      updateCustomFieldDefaultsEnabledQueryData(queryClient, variables.id, variables.enabled);
    },
  });
};

export const useUpsertCustomFieldDefault = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);
  const { user } = useContext(SignInContext);

  const mutationFn = async (variables: { id: string; fieldId: string; value: TCustomFieldRaw }): Promise<void> =>
    apiClient
      .put(`/documents/${variables.id}/custom-field-defaults/${variables.fieldId}`, { value: variables.value })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError(`Saving ${TERMINOLOGY.template.lower} default failed!`, true),
    onSuccess: (_data, variables) => updateCustomFieldDefaultQueryData(queryClient, variables, user._id),
  });
};

export const useTemplateUpdate = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    templateId?: string;
    bindingMode?: EBindingMode;
  }): Promise<TEditDraftReturn> =>
    apiClient
      .put(`/documents/${variables.id}/template-update`, {
        templateId: variables.templateId,
        bindingMode: variables.bindingMode,
      })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onError: handleApiError(`Failed to apply ${TERMINOLOGY.template.lower} update.`, true),
    onSuccess: (_data, variables) => {
      show('success', `${TERMINOLOGY.template.upper} applied successfully.`);
      // Template update restructures the entire draft (template source, field values, bindings).
      // The response is partial (TEditDraftReturn), so we invalidate rather than manually updating
      // the cache - unlike useSave/usePlaceBlock which do targeted setQueryData.
      queryClient.invalidateQueries({ queryKey: [`document-${variables.id}`] });
    },
  });
};

// --- DELETE:

export const useDeleteVariant = () => {
  const { apiClient, queryClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (variables: { docId: string; folder?: string }): Promise<void> => {
    await apiClient.delete(`/documents/${variables.docId}/variant?folder=${variables.folder || 'default'}`);

    queryClient.setQueryData([`document-${variables.docId}`], (oldData: TUseDocumentResult): TUseDocumentResult => {
      if (variables.folder)
        return {
          ...oldData,
          doc: {
            ...oldData.doc,
            draft: {
              ...(oldData.doc.draft as TDraft),
              variants: (oldData.doc.draft as TDraft).variants.filter((v) => v.folder !== variables.folder),
            },
          },
        };
      return {
        ...oldData,
        doc: { ...oldData.doc, draft: { ...(oldData.doc.draft as TDraft), hasDefaultVariant: false } },
      };
    });
  };

  return useMutation({ mutationFn, onError: handleApiError('Failed to delete variant!', true) });
};

export const useSetTaggedBlock = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: {
    id: string;
    blockTagId: string;
    blockId: string;
    blockItem?: TItem; // client-side only, used for resolvedItems cache update
  }): Promise<{
    selectedTaggedBlocks: TSelectedTaggedBlock[];
    stats: TStats;
    blockData: TBlockData;
  }> => {
    const res = await apiClient.put(`/documents/${variables.id}/tagged-block`, {
      blockTagId: variables.blockTagId,
      blockId: variables.blockId,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Failed to update tagged ${TERMINOLOGY.inlineBlock.lower}.`, true),
    onSuccess: (data, variables) => {
      show('success', `Tagged ${TERMINOLOGY.inlineBlock.lower} updated.`);
      queryClient.setQueryData(
        [`document-${variables.id}`],
        (oldData: TUseDocumentResult | undefined): TUseDocumentResult | undefined => {
          if (!oldData) return oldData;
          const existing = oldData.doc.blocksData[variables.blockId];
          const mergedBlockData = existing
            ? { ...existing, ...data.blockData, versions: { ...existing.versions, ...data.blockData.versions } }
            : data.blockData;
          return {
            ...oldData,
            resolvedItems: variables.blockItem
              ? {
                  ...oldData.resolvedItems,
                  [variables.blockId]: { item: variables.blockItem, resolvedHeadings: {} },
                }
              : oldData.resolvedItems,
            doc: {
              ...oldData.doc,
              draft: oldData.doc.draft
                ? { ...oldData.doc.draft, selectedTaggedBlocks: data.selectedTaggedBlocks }
                : undefined,
              versions: oldData.doc.draft
                ? oldData.doc.versions
                : oldData.doc.versions.map((v, i) =>
                    i === 0 ? { ...v, selectedTaggedBlocks: data.selectedTaggedBlocks } : v
                  ),
              stats: { ...oldData.doc.stats, modifiedDate: data.stats.modifiedDate },
              blocksData: { ...oldData.doc.blocksData, [variables.blockId]: mergedBlockData },
            },
          };
        }
      );
    },
  });
};
